// import React, { useEffect, useState, useMemo } from 'react';
// import { useSearchParams, useNavigate } from 'react-router-dom';
// import Header from '../components/Header/Header';
// import Background from '../components/Background/Background';
// import SearchBar from '../components/SearchBar/SearchBar';
// import MedicationList from '../components/MedicationList/MedicationList';
// import { Medication } from '../components/MedicationRow/MedicationRow';
// import AddMedicationForm from '../components/AddMedicationForm/AddMedicationForm';
// import ConfirmChangesModal, {
//   DecisionsMap,
// } from '../components/ConfirmChangesModal/ConfirmChangesModal';
// import '../styles/DoctorDashboard.css';

// interface Patient {
//   name: string;
//   ssn: string;
//   pharmacy: string;
//   nextDispensionDate: string;
//   medications: Medication[];
// }

// type NewMed = {
//   name: string;
//   times: string[];
//   indication: string;
//   instruction: string;
//   removed?: boolean;
// };

// const DoctorDashboard: React.FC = () => {
//   const [searchParams] = useSearchParams();
//   const navigate = useNavigate();
//   const initialSSN = searchParams.get('kennitala') || '';

//   const [activeTab, setActiveTab] = useState<'medications' | 'history'>('medications');

//   const [patient, setPatient] = useState<Patient | null>(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState('');
//   const [searchSSN, setSearchSSN] = useState(initialSSN);

//   // --- New state copied / inspired from DoctorDashboardLite ---
//   const [showAddForm, setShowAddForm] = useState(false);
//   //const [showEditForm, setShowEditForm] = useState(false);
//   const [stagedMeds, setStagedMeds] = useState<Medication[]>([]);
//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [editingSourceMed, setEditingSourceMed] = useState<Medication | null>(null);

//   // ---------- Helpers to create Medication objects (from Lite) ----------

//   const newMedicationFromForm = (med: NewMed): Medication => {
//     const id = `med-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
//     const timesObj: Record<string, number | ''> = {};
//     med.times.forEach((t) => (timesObj[t] = 1));

//     return {
//       id,
//       name: med.name,
//       times: timesObj,
//       indication: med.indication,
//       instruction: med.instruction,
//       isNew: true,
//     };
//   };

//   const editFromForm = (med: NewMed): Medication => {
//     const id = `edit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
//     const timesObj: Record<string, number | ''> = {};
//     med.times.forEach((t) => (timesObj[t] = 1));
//     return {
//       id,
//       name: med.name,
//       times: timesObj,
//       indication: med.indication,
//       instruction: med.instruction,
//       isEdit: true,
//     };
//   };

//   // ---------- Handlers for add / edit (same semantics as Lite) ----------

//   const handleAddMedication = (med: NewMed) => {
//     setStagedMeds((prev) => [...prev, newMedicationFromForm(med)]);
//     setShowAddForm(false);
//   };

//   const handleEditMedication = (med: NewMed) => {
//     if (!editingSourceMed) return;

//     // CASE 1 — REMOVAL MODE
//     if (med.removed) {
//       const removedMed: Medication = {
//         ...editingSourceMed,
//         times: {},
//         indication: med.indication,
//         instruction: med.instruction,
//         isRemoval: true,
//         isEdit: false,
//         isNew: false,
//       };

//       setStagedMeds((prev) => {
//         // remove any previous staged edits for this ID
//         const filtered = prev.filter((m) => m.id !== removedMed.id);
//         return [...filtered, removedMed];
//       });

//       setEditingSourceMed(null);
//       return;
//     }

//     // CASE 2 — NORMAL EDIT
//     const timesObj: Record<string, number | ''> = {};
//     med.times.forEach((t) => {
//       timesObj[t] = 1;
//     });

//     const editedMed: Medication = {
//       ...editingSourceMed,
//       name: med.name,
//       times: timesObj,
//       indication: med.indication,
//       instruction: med.instruction,
//       isEdit: true,
//       isRemoval: false,
//       isNew: false,
//     };

//     setStagedMeds((prev) => {
//       const filtered = prev.filter((m) => m.id !== editedMed.id);
//       return [...filtered, editedMed];
//     });

//     setEditingSourceMed(null);
//   };

//   // Submit after confirm modal (uses same payload structure as Lite).
//   const handleSubmitAll = async (decisions: DecisionsMap) => {
//     if (!patient) return;

//     const adds = stagedMeds.filter((m) => m.isNew);
//     const edits = stagedMeds.filter((m) => m.isEdit);
//     const removes = stagedMeds.filter((m) => m.isRemoval);

//     const mapEff = (mId: string) => (decisions[mId] === 'now' ? 'immediate' : 'next_cycle');

//     const formattedAdds = adds.flatMap((m) =>
//       Object.entries(m.times).map(([hour, dose]) => ({
//         name: m.name,
//         time: `${hour.padStart(2, '0')}:00`,
//         dose: dose || 1,
//         indication: m.indication,
//         instruction: m.instruction,
//         effective: mapEff(m.id),
//       }))
//     );

//     const formattedEdits = edits.flatMap((m) =>
//       Object.entries(m.times).map(([hour, dose]) => ({
//         name: m.name,
//         time: `${hour.padStart(2, '0')}:00`,
//         dose: dose || 1,
//         indication: m.indication,
//         instruction: m.instruction,
//         effective: mapEff(m.id),
//       }))
//     );

//     const formattedRemoves = removes.map((m) => ({
//       name: m.name,
//       effective: mapEff(m.id),
//       reason: m.instruction, // optional: send the reason text too
//     }));

//     const payload = {
//       ssn: patient.ssn,
//       pharmacy: patient.pharmacy,
//       // provider could be added here if you have it on the patient object
//       actions: {
//         add: formattedAdds,
//         edit: formattedEdits,
//         remove: formattedRemoves,
//       },
//     };

//     try {
//       // 🔁 Adjust this endpoint to match whatever your backend expects
//       const res = await fetch('http://localhost:4000/api/medication-changes', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload),
//       });

//       if (!res.ok) throw new Error('Villa við að senda breytingu');

//       alert('Breyting verður send til apóteks.');
//       setStagedMeds([]);
//       setShowConfirmModal(false);
//       // Re-fetch patient to refresh medication list
//       fetchPatient(patient.ssn);
//     } catch (err) {
//       console.error(err);
//       alert('Ekki tókst að senda breytingu');
//     }
//   };

//   // ---------- Fetching patient (same as original) ----------

//   const fetchPatient = async (ssn: string) => {
//     setLoading(true);
//     setError('');
//     try {
//       const res = await fetch(`http://localhost:4000/api/patients/${ssn}`);
//       if (!res.ok) throw new Error('Sjúklingur fannst ekki');

//       const data = await res.json();
//       setPatient(data);
//     } catch (err) {
//       console.error(err);
//       navigate('/notfound');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (!initialSSN) return;
//     fetchPatient(initialSSN);
//   }, [initialSSN]);

//   const handleSearch = async () => {
//     if (!searchSSN.trim()) return;
//     await fetchPatient(searchSSN.trim());
//   };

//   //   // Medications shown: original + staged (green / edited etc.)
//   //   const combinedMeds: Medication[] = useMemo(
//   //     () => [...(patient?.medications || []), ...stagedMeds],
//   //     [patient, stagedMeds]
//   //   );

//   const combinedMeds = useMemo(() => {
//     const base = patient?.medications || [];

//     // map of medId -> staged override (edit OR removal)
//     const overrides = new Map<string, Medication>();
//     stagedMeds.forEach((m) => {
//       if (m.isEdit || m.isRemoval) {
//         overrides.set(m.id, m);
//       }
//     });

//     // base meds, but replaced with staged edits/removals when they exist
//     const withOverrides = base.map((m) => overrides.get(m.id) ?? m);

//     // staged "new" meds still appear as extra rows at the bottom
//     const adds = stagedMeds.filter((m) => m.isNew);

//     return [...withOverrides, ...adds];
//   }, [patient, stagedMeds]);

//   return (
//     <div className="dashboard">
//       <Header
//         title="Mitoflux Rx"
//         userName="Teitur Ari Theodórsson"
//         center={
//           <SearchBar
//             value={searchSSN}
//             onChange={setSearchSSN}
//             onSearch={handleSearch}
//             onMenuClick={() => console.log('menu')}
//           />
//         }
//       />

//       <div className="tabs">
//         <button
//           className={`tab ${activeTab === 'medications' ? 'active' : ''}`}
//           onClick={() => {
//             setActiveTab('medications');
//             navigate('/');
//           }}
//         >
//           Lyfjaskammtanir
//         </button>
//         <button
//           className={`tab ${activeTab === 'history' ? 'active' : ''}`}
//           onClick={() => {
//             setActiveTab('history');
//             navigate('/doctor/changes');
//           }}
//         >
//           Þínar fyrri breytingar
//         </button>
//       </div>
//       <main className="dashboard-main">
//         <Background>
//           {loading && <div>Hleð upplýsingum...</div>}
//           {error && <div className="error">{error}</div>}

//           {!loading && patient && (
//             <>
//               <div className="patient-info">
//                 <div className="patient-name">{patient.name}</div>
//                 <div className="patient-ssn">kt. {patient.ssn}</div>
//                 <div className="pharmacy-name">{patient.pharmacy}</div>
//               </div>

//               <div className="next-visit">
//                 <span className="next-visit-label">Næsta skömmtun sjúklings hefst</span>
//                 <span className="next-visit-date">{patient.nextDispensionDate}</span>
//               </div>

//               <MedicationList
//                 medications={combinedMeds}
//                 expandedMedId={editingSourceMed?.id ?? null}
//                 onMedicationClick={(med: Medication) => {
//                   setShowAddForm(false);
//                   // Clicking the same row again could collapse it if you like:
//                   if (editingSourceMed && editingSourceMed.id === med.id) {
//                     setEditingSourceMed(null);
//                   } else {
//                     setEditingSourceMed(med);
//                   }
//                 }}
//                 renderExpandedRow={(med) => (
//                   <AddMedicationForm
//                     mode="edit"
//                     initialValues={{
//                       name: med.name,
//                       // Convert whatever the backend gives into plain hour strings for the buttons
//                       times: Object.entries(med.times)
//                         .filter(([_, dose]) => !!dose)
//                         .map(([hour]) => {
//                           // hour may be "9", "09", "09:00" etc → normalise to "9", "22", ...
//                           const hh = hour.length >= 2 ? hour.slice(0, 2) : hour;
//                           const num = parseInt(hh, 10);
//                           return String(num);
//                         }),
//                       indication: med.indication,
//                       instruction: med.instruction,
//                     }}
//                     onConfirm={(payload) => {
//                       handleEditMedication(payload); // your existing logic that stages an edit
//                       setEditingSourceMed(null);
//                     }}
//                     onCancel={() => setEditingSourceMed(null)}
//                   />
//                 )}
//               />

//               {showAddForm && (
//                 <AddMedicationForm
//                   mode="add"
//                   onConfirm={handleAddMedication}
//                   onCancel={() => setShowAddForm(false)}
//                 />
//               )}

//               <button
//                 className="add-med-btn"
//                 onClick={() => {
//                   setShowAddForm(true);
//                   //   setShowEditForm(false);
//                   setEditingSourceMed(null);
//                 }}
//               >
//                 Bæta við lyfi í skömmtun <span className="plus-btn"> +</span>
//               </button>

//               <button
//                 className="submit-btn"
//                 onClick={() => setShowConfirmModal(true)}
//                 disabled={stagedMeds.length === 0}
//               >
//                 Senda í skömmtun
//               </button>

//               {showConfirmModal && (
//                 <ConfirmChangesModal
//                   meds={stagedMeds.map((m) => ({
//                     id: m.id,
//                     name: m.name,
//                     instruction: m.isNew || m.isEdit ? m.instruction || '' : '',
//                     isNew: m.isNew,
//                     isEdit: m.isEdit,
//                     isRemoval: m.isRemoval,
//                   }))}
//                   onCancel={() => setShowConfirmModal(false)}
//                   onConfirm={(decisions) => handleSubmitAll(decisions)}
//                 />
//               )}
//             </>
//           )}
//         </Background>
//       </main>
//     </div>
//   );
// };

// export default DoctorDashboard;

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import SearchBar from '../components/SearchBar/SearchBar';
import MedicationList from '../components/MedicationList/MedicationList';
import { Medication } from '../components/MedicationRow/MedicationRow';
import AddMedicationForm from '../components/AddMedicationForm/AddMedicationForm';
import ConfirmChangesModal, {
  DecisionsMap,
} from '../components/ConfirmChangesModal/ConfirmChangesModal';
import PauseDispensingModal from '../components/PauseDispensingModal/PauseDispensingModal';
import '../styles/DoctorDashboard.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

interface Patient {
  name: string;
  ssn: string;
  pharmacyName: string;
  pharmacyId?: string;
  pharmacyOrgId?: string;
  nextDispensionDate: string;
  medications: Medication[];
  deliveryMethod?: string;
  phone?: string;
  priority?: string;
  dispenser?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  store?: string;
}

// Match what AddMedicationForm sends in onConfirm
type NewMed = {
  name: string;
  times: string[];
  indication: string;
  instruction: string;
  removed?: boolean;

  scheduleType?: 'daily' | 'weekly';
  doses?: Record<string, number>;
  weekdays?: number[];
  advanced?: Record<number, Record<string, number>>;
  endDate?: { year: number; month: number; day: number };
};

type WorkOrderType = 'CHANGE' | 'REGISTRATION' | 'DISPENSING_STATUS';
type ItemAction = 'ADD' | 'EDIT' | 'REMOVE' | 'PAUSE' | 'RESUME';

type CreateWorkOrderItem = {
  action: ItemAction;
  medicationIdentifier: string;
  proposedPayload: any;
};

type RegistrationDetails = {
  deliveryType: 'sott' | 'sent';
  demographics: {
    name: string;
    ssn: string;
    phone?: string;
  };
  deliveryDetails?: {
    // only if sent
    address?: string;
    postalCode?: string;
    city?: string;
    // only if sott
    store?: string;
    dispenser?: string;
    priority?: string;
  };
};

//?
type CreateWorkOrderRequest = {
  orderType: WorkOrderType;
  patientId: string;
  confirmPrescriptions: boolean;
  items: CreateWorkOrderItem[];
};
const DoctorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const location = useLocation();
  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();

  // Optional: state passed from LandingPage navigate(..., { state: { name, pharmacy } })
  const navState = (location.state as any) || null;
  const isNewPatient = patientId === 'new' || navState?.mode === 'new-patient';
  const resolvedPatientId =
    patientId && patientId !== 'new' ? patientId : (navState?.patientId as string | undefined);

  // Pausing the subscription/dispension
  const [isSubscriptionPaused, setIsSubscriptionPaused] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const openPauseConfirm = () => setShowPauseConfirm(true);
  const cancelPauseConfirm = () => setShowPauseConfirm(false);

  const confirmPause = async () => {
    const pid = resolvedPatientId;
    if (!pid) {
      alert('Vantar patientId.');
      return;
    }

    try {
      await pauseDispensing(pid);
      setIsSubscriptionPaused(true);
      setShowPauseConfirm(false);
    } catch (err) {
      console.error(err);
      alert('Ekki tókst að stöðva skömmtun tímabundið.');
    }
  };

  const resumeDosing = async () => {
    const pid = resolvedPatientId;
    if (!pid) {
      alert('Vantar patientId.');
      return;
    }

    try {
      await resumeDispensing(pid);
      setIsSubscriptionPaused(false);
    } catch (err) {
      console.error(err);
      alert('Ekki tókst að endurvekja skömmtun.');
    }
  };

  //const initialSSN = searchParams.get('kennitala') || '';

  const [activeTab, setActiveTab] = useState<'medications' | 'history'>('medications');

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchSSN, setSearchSSN] = useState('');

  // --- Staging / UI state ---
  const [showAddForm, setShowAddForm] = useState(false);
  const [stagedMeds, setStagedMeds] = useState<Medication[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingSourceMed, setEditingSourceMed] = useState<Medication | null>(null);

  // ---------- Helpers to create Medication objects from form ----------

  const newMedicationFromForm = (med: NewMed): Medication => {
    const id = `med-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    // Build times object with the *real* dose if provided
    const timesObj: Record<string, number | ''> = {};
    med.times.forEach((t) => {
      const dose = med.doses?.[t] ?? 1;
      timesObj[t] = dose;
    });

    return {
      id,
      name: med.name,
      times: timesObj,
      indication: med.indication,
      instruction: med.instruction,
      isNew: true,

      // carry structured meta so we can rehydrate later
      scheduleType: med.scheduleType,
      doses: med.doses,
      weekdays: med.weekdays,
      advanced: med.advanced,
      endDate: med.endDate,
    };
  };

  // ---------- Handlers for add / edit ----------

  const handleAddMedication = (med: NewMed) => {
    setStagedMeds((prev) => [...prev, newMedicationFromForm(med)]);
    setShowAddForm(false);
  };

  const handleEditMedication = (med: NewMed) => {
    if (!editingSourceMed) return;

    // ----- CASE 1: removal -----
    if (med.removed) {
      // If this med only exists locally (a newly added one), just drop it
      if (editingSourceMed.isNew) {
        setStagedMeds((prev) => prev.filter((m) => m.id !== editingSourceMed.id));
      } else {
        // Real removal of an existing medication
        const removedMed: Medication = {
          ...editingSourceMed,
          times: {},
          indication: med.indication,
          instruction: med.instruction,
          isRemoval: true,
          isEdit: false,
          isNew: false,
        };

        setStagedMeds((prev) => {
          const filtered = prev.filter((m) => m.id !== removedMed.id);
          return [...filtered, removedMed];
        });
      }

      setEditingSourceMed(null);
      return;
    }

    // Build times map with real doses (if provided)
    const timesObj: Record<string, number | ''> = {};
    med.times.forEach((t) => {
      const dose = med.doses?.[t] ?? 1;
      timesObj[t] = dose;
    });

    // ----- CASE 2A: editing a newly-added staged med -----
    if (editingSourceMed.isNew) {
      const updated: Medication = {
        ...editingSourceMed,
        name: med.name,
        times: timesObj,
        indication: med.indication,
        instruction: med.instruction,
        isNew: true,
        isEdit: false,
        isRemoval: false,
        scheduleType: med.scheduleType,
        doses: med.doses,
        weekdays: med.weekdays,
        advanced: med.advanced,
        endDate: med.endDate,
      };

      setStagedMeds((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      setEditingSourceMed(null);
      return;
    }

    // ----- CASE 2B: editing an existing medication from the backend -----
    const editedMed: Medication = {
      ...editingSourceMed,
      name: med.name,
      times: timesObj,
      indication: med.indication,
      instruction: med.instruction,
      isEdit: true,
      isRemoval: false,
      isNew: false,
      scheduleType: med.scheduleType,
      doses: med.doses,
      weekdays: med.weekdays,
      advanced: med.advanced,
      endDate: med.endDate,
    };

    setStagedMeds((prev) => {
      const filtered = prev.filter((m) => m.id !== editedMed.id);
      return [...filtered, editedMed];
    });

    setEditingSourceMed(null);
  };

  // ---------- Submit after confirm modal ----------

  const handleSubmitAll = async (decisions: DecisionsMap, confirmPrescriptions: boolean) => {
    if (!patient) return;

    if (!confirmPrescriptions) {
      alert('Vinsamlegast staðfestu að lyfseðlar hafi verið sendir.');
      return;
    }

    const adds = stagedMeds.filter((m) => m.isNew);
    const edits = stagedMeds.filter((m) => m.isEdit);
    const removes = stagedMeds.filter((m) => m.isRemoval);

    const mapEff = (mId: string) => (decisions[mId] === 'now' ? 'immediate' : 'next_cycle');

    const toTimeDoses = (m: Medication) =>
      Object.entries(m.times || {}).map(([hour, dose]) => ({
        time: `${String(hour).padStart(2, '0')}:00`,
        dose: typeof dose === 'number' ? dose : 1,
      }));

    // Build WorkOrder items (DB shape)
    const items: CreateWorkOrderItem[] = [];

    // ADD
    for (const m of adds) {
      items.push({
        action: 'ADD',
        medicationIdentifier: m.name, // or a real identifier later
        proposedPayload: {
          instruction: m.instruction ?? '',
          indication: m.indication ?? '',
          effective: mapEff(m.id),
          timeDoses: toTimeDoses(m),
          scheduleType: (m as any).scheduleType ?? null,
          doses: (m as any).doses ?? null,
          weekdays: (m as any).weekdays ?? null,
          advanced: (m as any).advanced ?? null,
          endDate: (m as any).endDate ?? null,
        },
      });
    }

    // EDIT
    for (const m of edits) {
      items.push({
        action: 'EDIT',
        medicationIdentifier: m.name,
        proposedPayload: {
          instruction: m.instruction ?? '',
          indication: m.indication ?? '',
          effective: mapEff(m.id),
          timeDoses: toTimeDoses(m),
          scheduleType: (m as any).scheduleType ?? null,
          doses: (m as any).doses ?? null,
          weekdays: (m as any).weekdays ?? null,
          advanced: (m as any).advanced ?? null,
          endDate: (m as any).endDate ?? null,
        },
      });
    }

    // REMOVE
    for (const m of removes) {
      items.push({
        action: 'REMOVE',
        medicationIdentifier: m.name,
        proposedPayload: {
          effective: mapEff(m.id),
          reason: m.instruction ?? '',
        },
      });
    }

    // Decide orderType:
    const orderType: WorkOrderType = isNewPatient ? 'REGISTRATION' : 'CHANGE';

    // Only include registrationDetails when REGISTRATION
    let registrationDetails: RegistrationDetails | undefined = undefined;

    if (orderType === 'REGISTRATION') {
      registrationDetails = {
        deliveryType: (patient.deliveryMethod as 'sott' | 'sent') ?? 'sott',
        demographics: {
          name: patient.name,
          ssn: patient.ssn,
          phone: patient.phone ?? undefined,
        },
        deliveryDetails: {
          address: patient.address ?? undefined,
          postalCode: patient.postalCode ?? undefined,
          city: patient.city ?? undefined,
          store: patient.store ?? undefined,
          dispenser: patient.dispenser ?? undefined,
          priority: patient.priority ?? undefined,
        },
      };
    }

    // IMPORTANT: use patientId from route param or state.
    // If it is in URL as /doctor/patient/:patientId, prefer that.
    const pid =
      patientId && patientId !== 'new' ? patientId : (navState?.patientId as string | undefined);

    try {
      const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/work-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderType,
          patientId: pid, // preferred
          ssn: patient.ssn, // safety-net (backend can map via vault if needed)
          pharmacyId: patient.pharmacyId,
          items,
          registrationDetails,
          confirmPrescriptions: true,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`WorkOrders API ${res.status}: ${text}`);
      }

      alert('Breyting hefur verið send til apóteks.');
      setStagedMeds([]);
      setShowConfirmModal(false);

      // Optional: refetch patient
      if (patientId && patientId !== 'new') fetchPatientById(patientId);
    } catch (err) {
      console.error(err);
      alert('Ekki tókst að senda breytingu');
    }
  };
  //json file
  // const handleSubmitAll = async (decisions: DecisionsMap) => {
  //   if (!patient) return;

  //   const adds = stagedMeds.filter((m) => m.isNew);
  //   const edits = stagedMeds.filter((m) => m.isEdit);
  //   const removes = stagedMeds.filter((m) => m.isRemoval);

  //   const mapEff = (mId: string) => (decisions[mId] === 'now' ? 'immediate' : 'next_cycle');

  //   const toTimeDoses = (m: Medication) =>
  //     Object.entries(m.times || {}).map(([hour, dose]) => ({
  //       time: `${String(hour).padStart(2, '0')}:00`,
  //       dose: typeof dose === 'number' ? dose : 1,
  //     }));

  //   const toAction = (m: Medication) => ({
  //     name: m.name,
  //     instruction: m.instruction,
  //     indication: m.indication ?? '',
  //     effective: mapEff(m.id),

  //     // NEW: keep per-med list of times (can be empty)
  //     timeDoses: toTimeDoses(m),

  //     // NEW: keep structured meta for later DB/FHIR work
  //     scheduleType: (m as any).scheduleType ?? null,
  //     doses: (m as any).doses ?? null,
  //     weekdays: (m as any).weekdays ?? null,
  //     advanced: (m as any).advanced ?? null,
  //     endDate: (m as any).endDate ?? null,
  //   });

  //   const formattedAdds = adds.map(toAction);
  //   const formattedEdits = edits.map(toAction);

  //   const formattedRemoves = removes.map((m) => ({
  //     name: m.name,
  //     effective: mapEff(m.id),
  //     reason: m.instruction, // keep your “reason”
  //   }));

  //   const payload = {
  //     ssn: patient.ssn,
  //     patientName: patient.name,
  //     pharmacy: patient.pharmacy,
  //     sender: 'Læknir',
  //     phone: '—',
  //     actions: {
  //       add: formattedAdds,
  //       edit: formattedEdits,
  //       remove: formattedRemoves,
  //     },
  //   };

  //   try {
  //     const res = await fetchWithAuth(getAccessTokenSilently,`${API_BASE}/api/work-orders`,
  //       {
  //         method: 'POST',
  //         headers: { 'Content-Type': 'application/json' },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!res.ok) {
  //       const text = await res.text();
  //       throw new Error(`API ${res.status}: ${text}`);
  //     }

  //     alert('Breyting hefur verið send til apóteks.');
  //     setStagedMeds([]);
  //     setShowConfirmModal(false);
  //     if (patientId) fetchPatientById(patientId);
  //   } catch (err) {
  //     console.error(err);
  //     alert('Ekki tókst að senda breytingu');
  //   }
  // };

  // ---------- Fetching patient ----------

  useEffect(() => {
    setIsSubscriptionPaused(false);
  }, [patientId]);

  //hér kom villa því það vantaði pharmacyId í POST work-orders
  // useEffect(() => {
  //   if (!isNewPatient) return;

  //   setPatient({
  //     name: navState.name ?? '',
  //     ssn: navState?.ssn ?? '',
  //     pharmacyName: navState?.pharmacy ?? navState?.pharmacyName ?? '',
  //     nextDispensionDate: '', //ATH today + 2 dagar?
  //     medications: [],
  //     deliveryMethod: navState?.deliveryMethod ?? '',
  //     priority: navState?.priority ?? '',
  //     dispenser: navState?.dispenser ?? '',
  //   });

  //   setStagedMeds([]);
  //   setEditingSourceMed(null);
  //   setShowAddForm(false);
  // }, [isNewPatient]);

  useEffect(() => {
    if (!isNewPatient) return;

    setPatient({
      name: navState?.name ?? '',
      ssn: navState?.ssn ?? '',

      // IMPORTANT: ids from resolve
      pharmacyId: navState?.pharmacyId,
      pharmacyOrgId: navState?.pharmacyOrgId,

      // keep these separate to avoid "Lyfja"/"Lyfja"
      dispenser: navState?.dispenser ?? '',
      store: navState?.store ?? '',
      pharmacyName: navState?.pharmacyName ?? navState?.store ?? '',

      phone: navState?.phone ?? '',
      deliveryMethod: navState?.deliveryMethod ?? '',
      priority: navState?.priority ?? '',
      address: navState?.address ?? '',
      postalCode: navState?.postalCode ?? '',
      city: navState?.city ?? '',

      nextDispensionDate: '',
      medications: [],
    });

    setStagedMeds([]);
    setEditingSourceMed(null);
    setShowAddForm(false);
  }, [isNewPatient, navState]);

  const fetchPatientById = async (pid: string) => {
    setLoading(true);
    setError('');

    try {
      const res = await fetchWithAuth(
        getAccessTokenSilently,
        `${API_BASE}/api/patients/${encodeURIComponent(pid)}`
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }

      const data = await res.json();
      setPatient({
        ...data,
        pharmacyName: data.pharmacyName ?? data.pharmacy ?? '',
        pharmacyId: data.pharmacyId,
        pharmacyOrgId: data.pharmacyOrgId,
      });
    } catch {
      navigate('/notfound');
    } finally {
      setLoading(false);
    }
  };

  const pauseDispensing = async (pid: string) => {
    if (!patient?.pharmacyId) {
      alert('Vantar pharmacyId fyrir sjúkling.');
      return;
    }

    const res = await fetchWithAuth(
      getAccessTokenSilently,
      `${API_BASE}/api/patients/${encodeURIComponent(pid)}/dispensing/pause`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pharmacyId: patient?.pharmacyId,
          pharmacyOrgId: patient?.pharmacyOrgId,
        }),
      }
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Pause dispensing failed (${res.status}): ${text}`);
    }

    return res.json();
  };

  const resumeDispensing = async (pid: string) => {
    if (!patient?.pharmacyId) {
      alert('Vantar pharmacyId fyrir sjúkling.');
      return;
    }

    const res = await fetchWithAuth(
      getAccessTokenSilently,
      `${API_BASE}/api/patients/${encodeURIComponent(pid)}/dispensing/resume`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pharmacyId: patient?.pharmacyId,
          pharmacyOrgId: patient?.pharmacyOrgId,
        }),
      }
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Resume dispensing failed (${res.status}): ${text}`);
    }

    return res.json();
  };

  useEffect(() => {
    if (navState?.name && !patient) {
      setPatient(
        (prev) =>
          prev ?? {
            name: navState.name,
            ssn: '', // unknown until fetched
            pharmacyName: navState.pharmacy ?? navState.pharmacyName ?? '',
            nextDispensionDate: '',
            medications: [],
          }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (!patientId) return;
  //   fetchPatientById(patientId);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [patientId]);
  useEffect(() => {
    if (isNewPatient) return;
    if (!patientId) return;

    fetchPatientById(patientId);
  }, [patientId, isNewPatient]);

  const handleSearch = async () => {
    const ssnTrimmed = searchSSN.trim();
    if (!ssnTrimmed) return;
    if (!/^\d{10}$/.test(ssnTrimmed)) {
      alert('Kennitala verður að vera nákvæmlega 10 tölustafir');
      return;
    }

    try {
      const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/patients/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssn: ssnTrimmed }),
      });

      if (res.status === 404) {
        navigate('/notfound');
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }

      const data = await res.json();
      const newPatientId = data.patientId as string | undefined;
      if (!newPatientId) throw new Error('Missing patientId in search response');

      navigate(`/doctor/patient/${encodeURIComponent(newPatientId)}`, {
        state: { name: data.name, pharmacyName: data.pharmacyName ?? data.pharmacy ?? '' },
      });
    } catch {
      navigate('/notfound');
    }
  };

  // Medications shown: original + staged (green / edited / removed etc.)
  const combinedMeds = useMemo(() => {
    const base = patient?.medications || [];

    // map of medId -> staged override (edit OR removal)
    const overrides = new Map<string, Medication>();
    stagedMeds.forEach((m) => {
      if (m.isEdit || m.isRemoval) {
        overrides.set(m.id, m);
      }
    });

    // base meds, but replaced with staged edits/removals when they exist
    const withOverrides = base.map((m) => overrides.get(m.id) ?? m);

    // staged "new" meds still appear as extra rows at the bottom
    const adds = stagedMeds.filter((m) => m.isNew);

    return [...withOverrides, ...adds];
  }, [patient, stagedMeds, getAccessTokenSilently]);

  return (
    <div className="dashboard">
      <Header
        center={
          <SearchBar
            value={searchSSN}
            onChange={setSearchSSN}
            onSearch={handleSearch}
            onMenuClick={() => console.log('menu')}
            // ATH
          />
        }
      />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'medications' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('medications');
            navigate('/doctor');
          }}
        >
          Lyfjaskammtanir
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('history');
            navigate('/doctor/changes');
          }}
        >
          Þínar fyrri breytingar
        </button>
      </div>

      <main className="dashboard-main">
        <Background>
          {loading && <div>Hleð upplýsingum...</div>}
          {error && <div className="error">{error}</div>}

          {!loading && patient && (
            <>
              <div className="patient-info">
                <div className="patient-name">{patient.name}</div>
                <div className="patient-ssn">kt. {patient.ssn}</div>
                <div className="pharmacy-name">{patient.dispenser}</div>
                <div className="pharmacy-name">{patient.pharmacyName}</div>
              </div>

              <div className="next-visit">
                <div className="next-visit-top">
                  <span className="next-visit-label">Næsta skömmtun sjúklings hefst</span>

                  <span className={`next-visit-date ${isSubscriptionPaused ? 'paused' : ''}`}>
                    {patient.nextDispensionDate}
                  </span>
                </div>

                {/* Only show this control set when NOT creating a new patient */}
                {!isNewPatient && (
                  <div className="next-visit-actions">
                    {!isSubscriptionPaused ? (
                      <button type="button" className="pause-btn" onClick={openPauseConfirm}>
                        Stöðva skömmtun tímabundið
                      </button>
                    ) : (
                      <div className="paused-actions">
                        <button type="button" className="resume-btn" onClick={resumeDosing}>
                          Endurvekja skömmtun
                        </button>

                        <div className="paused-label" aria-label="Skömmtun stöðvuð">
                          Skömmtun stöðvuð
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <MedicationList
                medications={combinedMeds}
                expandedMedId={editingSourceMed?.id ?? null}
                onMedicationClick={(med: Medication) => {
                  setShowAddForm(false);
                  if (editingSourceMed && editingSourceMed.id === med.id) {
                    setEditingSourceMed(null);
                  } else {
                    // setEditingSourceMed((prev) => (prev?.id === med.id ? null : med)); //ATH mögulega betra útaf id?
                    setEditingSourceMed(med);
                  }
                }}
                renderExpandedRow={(med) => (
                  <AddMedicationForm
                    key={`edit-${med.id}`}
                    mode="edit"
                    initialValues={{
                      name: med.name,
                      // Convert times map into plain hour strings like "10", "16"
                      times: Object.entries(med.times)
                        .filter(([_, dose]) => !!dose)
                        .map(([hour]) => {
                          const hh = hour.length >= 2 ? hour.slice(0, 2) : hour;
                          const num = parseInt(hh, 10);
                          return String(num);
                        }),
                      indication: med.indication ?? '',
                      instruction: med.instruction ?? '',

                      // 👇 pass structured meta back into the form
                      scheduleType: med.scheduleType,
                      doses: med.doses as Record<string, number> | undefined,
                      weekdays: med.weekdays,
                      advanced: med.advanced,
                      endDate: med.endDate,
                    }}
                    onConfirm={(payload) => {
                      handleEditMedication(payload);
                      setEditingSourceMed(null);
                    }}
                    onCancel={() => setEditingSourceMed(null)}
                  />
                )}
              />

              {showAddForm && (
                <AddMedicationForm
                  //key="add-form"  //ATH veit ekki hvort þetta þurfi
                  mode="add"
                  onConfirm={handleAddMedication}
                  onCancel={() => setShowAddForm(false)}
                />
              )}

              <button
                className="add-med-btn"
                onClick={() => {
                  setShowAddForm(true);
                  setEditingSourceMed(null);
                }}
              >
                Bæta við lyfi í skömmtun <span className="plus-btn"> +</span>
              </button>

              <button
                className="submit-btn"
                onClick={() => setShowConfirmModal(true)}
                disabled={stagedMeds.length === 0}
              >
                Senda í skömmtun
              </button>

              {showConfirmModal && (
                <ConfirmChangesModal
                  meds={stagedMeds.map((m) => ({
                    id: m.id,
                    name: m.name,
                    instruction: m.isNew || m.isEdit ? m.instruction || '' : '',
                    isNew: m.isNew,
                    isEdit: m.isEdit,
                    isRemoval: m.isRemoval,
                  }))}
                  onCancel={() => setShowConfirmModal(false)}
                  onConfirm={(decisions, confirmPrescriptions) =>
                    handleSubmitAll(decisions, confirmPrescriptions)
                  }
                />
              )}
              {showPauseConfirm && (
                <PauseDispensingModal
                  onCancel={() => setShowPauseConfirm(false)}
                  // onConfirm={() => {
                  //   setIsSubscriptionPaused(true);
                  //   setShowPauseConfirm(false);
                  // }}
                  onConfirm={confirmPause}
                />
              )}
            </>
          )}
        </Background>
      </main>
    </div>
  );
};

export default DoctorDashboard;
